import { defaultMajor, defaultResearch } from '@components/profile/profileOverlay/overlays/edu/eduForm';
import {
  ProfileEducationUpdateVO,
  ProfileMajorUpdateVO,
  ProfileResearchUpdateVO,
} from '@domain/rq/profile/ProfileEducationRq';
import { EducationVO, MajorVO } from '@domain/vo/profile/ProfileEducationVO';
import MajorRs from '@domain/rs/profile/edu/MajorRs';
import {
  EducationLevelNameMap,
  EducationNameMap,
  EducationLevelType,
  EducationStatusType,
  MajorType,
  ScoreType,
  Score,
  EducationLevel,
} from '@domain/constant/profile/EducationType';
import { ReactSelectOption } from '@domain/constant/ReactSelectOption';
import DateFormat from '@utils/date/format';

export class ProfileMajorFormVO {
  majorAlias: ReactSelectOption<MajorRs> | null; // 학과
  majorType: MajorType; // 전공 타입
  scoreType: ScoreType; // 학점 타입
  score: string; // 학점
  perfectScore: string; // 만점

  constructor(dto: ProfileMajorFormVO) {
    this.majorAlias = dto.majorAlias;
    this.majorType = dto.majorType;
    this.scoreType = dto.scoreType;
    this.score = dto.score;
    this.perfectScore = dto.perfectScore;
  }
}

export class ProfileEducationFormVO {
  schoolLevel: ReactSelectOption<EducationLevelType> | null; // 학교 종류
  educationType: ReactSelectOption<EducationStatusType> | null; // 학력 상태
  transferYn: boolean;
  startDate?: string; // 재학 시작일시
  endDate?: string; // 재학 종료일시
  school: ReactSelectOption | null; // 학교명
  majorUseYn: boolean;
  major?: ProfileMajorFormVO; // 주전공
  minorUseYn: boolean;
  minor?: ProfileMajorFormVO; // 부전공
  doubleUseYn: boolean;
  double?: ProfileMajorFormVO; // 복수 전공
  profileResearches: ProfileResearchUpdateVO[]; // 연구 분야 및 논문 리스트

  constructor(dto: ProfileEducationFormVO) {
    this.schoolLevel = dto.schoolLevel;
    this.educationType = dto.educationType;
    this.transferYn = dto.transferYn;
    this.startDate = DateFormat.getDateFormatString(dto.startDate, 'yyyy-MM');
    this.endDate = DateFormat.getDateFormatString(dto.endDate, 'yyyy-MM');
    this.school = dto.school;
    this.majorUseYn = dto.majorUseYn;
    this.major = dto.major;
    this.minorUseYn = dto.minorUseYn;
    this.minor = dto.minor;
    this.doubleUseYn = dto.doubleUseYn;
    this.double = dto.double;
    this.profileResearches = dto.profileResearches.map((dto) => new ProfileResearchUpdateVO(dto));
  }

  static convertProfileEducationFormVO = (vo: EducationVO): ProfileEducationFormVO => {
    const major =
      ProfileEducationFormVO.convertProfileMajorFormVO(vo.profileMajors.find((e) => e.majorType === 'MAJOR')) ||
      defaultMajor;
    const minor = ProfileEducationFormVO.convertProfileMajorFormVO(
      vo.profileMajors.find((e) => e.majorType === 'MINOR'),
    );
    const double = ProfileEducationFormVO.convertProfileMajorFormVO(
      vo.profileMajors.find((e) => e.majorType === 'DOUBLE'),
    );
    const profileResearches = vo.profileResearches.length === 0 ? [defaultResearch] : vo.profileResearches;

    return new ProfileEducationFormVO({
      schoolLevel: { value: vo.schoolLevel, label: EducationLevelNameMap[vo.schoolLevel] },
      educationType: { value: vo.educationType, label: EducationNameMap[vo.educationType] },
      transferYn: vo.transferYn,
      startDate: DateFormat.getDateFormatString(vo.startDate, 'yyyy-MM'),
      endDate: DateFormat.getDateFormatString(vo.endDate, 'yyyy-MM'),
      school: { value: vo.schoolCode, label: vo.schoolName, __isNew__: vo.schoolCode === null },
      majorUseYn: !!major,
      major,
      minorUseYn: !!minor,
      minor,
      doubleUseYn: !!double,
      double,
      profileResearches,
    });
  };

  static convertProfileMajorFormVO = (vo?: MajorVO): ProfileMajorFormVO | undefined => {
    if (!vo) return undefined;
    return {
      majorAlias: {
        value: { majorCode: vo.majorCode, name: vo.majorName },
        label: vo.majorName,
      },
      majorType: vo.majorType,
      scoreType: vo.scoreType ?? Score.SCORE,
      score: vo.score ? String(vo.score) : '',
      perfectScore: vo.perfectScore ? String(vo.perfectScore) : '',
    };
  };

  static convertProfileEducationUpdateVO = (vo: ProfileEducationFormVO): ProfileEducationUpdateVO => {
    const profileMajors = [];
    if (vo.schoolLevel?.value !== EducationLevel.HIGHSCHOOL && vo.majorUseYn && vo.major)
      profileMajors.push(ProfileEducationFormVO.convertProfileMajorUpdateVO(vo.major));
    if (vo.schoolLevel?.value !== EducationLevel.HIGHSCHOOL && vo.minorUseYn && vo.minor)
      profileMajors.push(ProfileEducationFormVO.convertProfileMajorUpdateVO(vo.minor));
    if (vo.schoolLevel?.value !== EducationLevel.HIGHSCHOOL && vo.doubleUseYn && vo.double)
      profileMajors.push(ProfileEducationFormVO.convertProfileMajorUpdateVO(vo.double));

    return new ProfileEducationUpdateVO({
      schoolLevel: vo.schoolLevel?.value || null,
      educationType: vo.educationType?.value || null,
      transferYn: vo.transferYn,
      startDate: DateFormat.getDateFormatString(vo.startDate, 'yyyy-MM'),
      endDate: DateFormat.getDateFormatString(vo.endDate, 'yyyy-MM'),
      schoolCode: vo.school?.__isNew__ ? null : Number(vo.school?.value ?? -1), //직접 입력일 경우 null 처리
      schoolName: vo.school?.label || '',
      profileMajors,
      profileResearches:
        vo.profileResearches?.filter((dto) => {
          return dto.attachFiles?.length || dto.description;
        }) || [],
    });
  };

  static convertProfileMajorUpdateVO = (majorFormVO: ProfileMajorFormVO): ProfileMajorUpdateVO => {
    return new ProfileMajorUpdateVO({
      majorType: majorFormVO.majorType,
      majorCode: majorFormVO.majorAlias?.value.majorCode ? Number(majorFormVO.majorAlias?.value.majorCode) : null,
      majorName: majorFormVO.majorAlias?.value.name ?? '',
      scoreType: majorFormVO.scoreType,
      score: Number(majorFormVO.score ?? -1),
      perfectScore: Number(majorFormVO.perfectScore ?? -1),
    });
  };
}
